/**
 * /operations/leave/adjustments — Session 3a PR 5.
 *
 * HR-initiated leave balance adjustments (positive/negative) with full
 * audit trail. Tashghil-owned per Session 3a §4.4.
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (live fetch)
 *   2. InlineAISuggestion cards (positive-cluster + Article 109 floor)
 *   3. TraceableValue wrapping delta_days + monthly_running_balance (≥3)
 */

import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../../../components/Loading";
import {
  EntityDrawer,
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../../../components/ui";
import {
  session3aApi,
  type LeaveAdjustmentItem,
} from "../../../lib/session3a-api";
import { useLegalEntityGate } from "../../../lib/legalEntity";
import { AiBriefPanel } from "../../../shell/AiBriefPanel";
import { severityChip, showAlertTrace } from "../../_session3aHelpers";

const WINDOWS = [
  { days: 30, key: "last_30" },
  { days: 90, key: "last_90" },
] as const;

function NewAdjustmentDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    employee_id: "",
    leave_type_code: "ANNUAL",
    delta_days: "",
    reason: "",
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("operations.leave_adjustments.cta.new", "New adjustment")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            disabled={!form.employee_id || !form.delta_days || !form.reason}
            onClick={() => {
              showAlertTrace(
                t(
                  "operations.leave_adjustments.applied",
                  "Adjustment will write a leave_balance_event row with event_type='hr_adjustment' once the endpoint lands.",
                ),
              );
              onCreated();
              onClose();
            }}
            className="rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
          >
            {t("operations.leave_adjustments.apply", "Apply")}
          </button>
        </div>
      }
    >
      <div className="space-y-4 p-4 text-sm">
        <div className="rounded-lg border border-[#e9d5ff] bg-[#f5f3ff] px-3 py-2 text-xs text-[#6b21a8]">
          ✦{" "}
          {t(
            "operations.leave_adjustments.drawer_hint",
            "Tashghil drafts the reason; you stay in control of the final wording.",
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("operations.leave_adjustments.field.employee", "Employee")}
          </label>
          <input
            value={form.employee_id}
            onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
            placeholder="00000000-0000-0000-0000-000000000000"
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("operations.leave_adjustments.field.type", "Leave type")}
          </label>
          <select
            value={form.leave_type_code}
            onChange={(e) =>
              setForm({ ...form, leave_type_code: e.target.value })
            }
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
          >
            <option value="ANNUAL">{t("operations.leave_adjustments.type.annual", "Annual")}</option>
            <option value="SICK">{t("operations.leave_adjustments.type.sick", "Sick")}</option>
            <option value="HAJJ">{t("operations.leave_adjustments.type.hajj", "Hajj")}</option>
            <option value="MATERNITY">{t("operations.leave_adjustments.type.maternity", "Maternity")}</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("operations.leave_adjustments.field.delta", "Adjustment (days)")}
          </label>
          <input
            type="number"
            value={form.delta_days}
            onChange={(e) => setForm({ ...form, delta_days: e.target.value })}
            placeholder="e.g. +5 or -3"
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("operations.leave_adjustments.field.reason", "Reason")}
          </label>
          <textarea
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            rows={3}
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            placeholder={t(
              "operations.leave_adjustments.reason_placeholder",
              "Why is this adjustment necessary?",
            )}
          />
          <button
            type="button"
            onClick={() =>
              setForm((f) => ({
                ...f,
                reason: t(
                  "operations.leave_adjustments.draft_reason",
                  "Compensatory adjustment for days worked during public holidays.",
                ),
              }))
            }
            className="mt-1 text-[11px] text-[#2260C9] hover:underline"
          >
            ✦ {t("operations.leave_adjustments.draft", "Tashghil draft reason")}
          </button>
        </div>
      </div>
    </EntityDrawer>
  );
}

export function LeaveAdjustmentsPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [days, setDays] = useState<number>(30);
  const [sign, setSign] = useState<"all" | "positive" | "negative">("all");
  const [showDrawer, setShowDrawer] = useState(false);

  const list = useQuery({
    queryKey: ["leave-adjustments", legalEntityId, days],
    queryFn: () => session3aApi.listLeaveAdjustments(legalEntityId ?? "", days),
    enabled: !!legalEntityId,
  });

  const items: LeaveAdjustmentItem[] = list.data?.items ?? [];

  const stats = useMemo(() => {
    const positives = items.filter((r) => r.delta_days > 0);
    const negatives = items.filter((r) => r.delta_days < 0);
    const sumDelta = items.reduce((acc, r) => acc + r.delta_days, 0);
    const terseReasons = items.filter(
      (r) => (r.reason ?? "").trim().length < 10,
    ).length;
    return {
      total: items.length,
      positives: positives.length,
      negatives: negatives.length,
      sumDelta,
      terseReasons,
      bigPositiveCluster: items
        .reduce<Record<string, number>>((acc, r) => {
          if (r.delta_days > 0) acc[r.employee_id] = (acc[r.employee_id] ?? 0) + 1;
          return acc;
        }, {}),
    };
  }, [items]);

  const clusterEmployee = useMemo(() => {
    const entries = Object.entries(stats.bigPositiveCluster);
    const sorted = entries.sort((a, b) => b[1] - a[1]);
    return sorted.length && sorted[0][1] >= 3
      ? items.find((r) => r.employee_id === sorted[0][0])
      : undefined;
  }, [stats.bigPositiveCluster, items]);

  const filtered = useMemo(() => {
    if (sign === "positive") return items.filter((r) => r.delta_days > 0);
    if (sign === "negative") return items.filter((r) => r.delta_days < 0);
    return items;
  }, [items, sign]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("operations.leave_adjustments.title", "Leave Adjustments")}
          subtitle={t(
            "operations.leave_adjustments.subtitle",
            "HR-initiated leave balance adjustments with audit trail. Every event writes to leave_balance_events.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {WINDOWS.map(({ days: d, key }) => (
              <button
                key={key}
                type="button"
                onClick={() => setDays(d)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  days === d
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`operations.leave_adjustments.filter.${key}`, `Last ${d} days`)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSign(sign === "positive" ? "all" : "positive")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                sign === "positive"
                  ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("operations.leave_adjustments.filter.positive", "Positive only")}
            </button>
            <button
              type="button"
              onClick={() => setSign(sign === "negative" ? "all" : "negative")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                sign === "negative"
                  ? "border-rose-300 bg-rose-50 text-rose-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("operations.leave_adjustments.filter.negative", "Negative only")}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowDrawer(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("operations.leave_adjustments.cta.new", "New adjustment")}
          </button>
        </div>

        {clusterEmployee && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<AlertCircle className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "operations.leave_adjustments.suggestion.cluster",
              `Pattern: ${clusterEmployee.employee_name_en} received 3+ positive adjustments in ${days} days.`,
            )}
            body={t(
              "operations.leave_adjustments.suggestion.cluster_body",
              "Audit recommended. Open the employee balance history to verify.",
            )}
            primaryAction={{
              label: t(
                "operations.leave_adjustments.suggestion.open_balance",
                "Open balance history",
              ),
              onClick: () =>
                window.location.assign(
                  `/operations/leave/balances?employee_id=${clusterEmployee.employee_id}`,
                ),
            }}
            suggestionKey={`leave-adjustments-cluster-${clusterEmployee.employee_id}`}
          />
        )}
        {stats.negatives > 0 && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<Search className="h-4 w-4" />}
            severity="info"
            headline={t(
              "operations.leave_adjustments.suggestion.floor_headline",
              "Article 109 floor reminder.",
            )}
            body={t(
              "operations.leave_adjustments.suggestion.floor_body",
              `${stats.negatives} negative adjustments recorded; Mutaba'aa watches that no employee falls below the 21-day annual minimum.`,
            )}
            primaryAction={{
              label: t(
                "operations.leave_adjustments.suggestion.scan_floor",
                "Review floor exposure",
              ),
              onClick: () =>
                showAlertTrace(
                  t(
                    "operations.leave_adjustments.suggestion.floor_note",
                    "Floor scanner aggregates today's balances; negative deltas that would breach 21 days are auto-flagged.",
                  ),
                ),
            }}
            suggestionKey="leave-adjustments-floor"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">
                    {t("operations.leave_adjustments.col.employee", "Employee")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.leave_adjustments.col.leave_type", "Leave type")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.leave_adjustments.col.delta", "Adjustment")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.leave_adjustments.col.reason", "Reason")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.leave_adjustments.col.hr_actor", "HR actor")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.leave_adjustments.col.adjusted_at", "Adjusted at")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.leave_adjustments.col.source", "Source")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.event_id}
                    className="border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                      <div className="text-xs text-text-tertiary">{row.employee_code}</div>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{row.leave_type_name_en}</td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.leave_adjustments.trace.delta",
                              `delta_days = ${row.delta_days}. Writes a leave_balance_event with event_type='hr_adjustment' linking back to this audit row.`,
                            ),
                          )
                        }
                      >
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            row.delta_days > 0
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {row.delta_days > 0 ? "+" : ""}
                          {row.delta_days}{" "}
                          {t("operations.leave_adjustments.days", "days")}
                        </span>
                      </TraceableValue>
                    </td>
                    <td
                      className="px-3 py-2 text-xs text-text-secondary"
                      title={row.reason}
                    >
                      {(row.reason ?? "").length > 60
                        ? `${row.reason.slice(0, 60)}…`
                        : row.reason}
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{row.hr_actor_name}</td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.leave_adjustments.trace.audit_row",
                              `Adjusted at ${row.adjusted_at}. Full audit row preserves before/after balance + actor IP.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr">{row.adjusted_at.slice(0, 10)}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(
                          row.source === "hr_initiated"
                            ? "info"
                            : row.source === "concierge"
                              ? "info"
                              : "neutral",
                        )}`}
                      >
                        {row.source}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "operations.leave_adjustments.empty",
              "No adjustments in this period. ✦ Tashghil notes: balances are accruing normally.",
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: stats.total,
            attention: stats.terseReasons,
            critical: 0,
            top_kind: t("operations.leave_adjustments.title", "Leave Adjustments"),
            window_label: `last ${days} days`,
          }}
        />
      </aside>
      {showDrawer && (
        <NewAdjustmentDrawer
          onClose={() => setShowDrawer(false)}
          onCreated={() => list.refetch()}
        />
      )}
    </div>
  );
}
